import { ArrowUpDown, Check } from "lucide-react";
import { Button } from "@/v2/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";
import { cn } from "@/v2/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SortMode =
	| "time-desc"
	| "time-asc"
	| "duration-desc"
	| "duration-asc"
	| "matter-asc"
	| "matter-desc";

export type GroupMode = "hour" | "matter" | "application" | "none";

export const DEFAULT_SORT: SortMode = "time-desc";
export const DEFAULT_GROUP: GroupMode = "hour";

export function isSortActive(sort: SortMode, group: GroupMode): boolean {
	return sort !== DEFAULT_SORT || group !== DEFAULT_GROUP;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SortPopoverProps {
	sort: SortMode;
	group: GroupMode;
	onSortChange: (sort: SortMode) => void;
	onGroupChange: (group: GroupMode) => void;
}

const sortOptions: { value: SortMode; label: string }[] = [
	{ value: "time-desc", label: "Newest first" },
	{ value: "time-asc", label: "Oldest first" },
	{ value: "duration-desc", label: "Duration (high to low)" },
	{ value: "duration-asc", label: "Duration (low to high)" },
	{ value: "matter-asc", label: "Matter (A to Z)" },
	{ value: "matter-desc", label: "Matter (Z to A)" },
];

const groupOptions: { value: GroupMode; label: string }[] = [
	{ value: "hour", label: "Hour" },
	{ value: "matter", label: "Matter" },
	{ value: "application", label: "Application" },
];

export function SortPopover({
	sort,
	group,
	onSortChange,
	onGroupChange,
}: SortPopoverProps) {
	const active = isSortActive(sort, group);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative h-10 w-10 text-muted-foreground"
				>
					<ArrowUpDown className="size-5" />
					{active && (
						<span className="absolute top-1 right-1 size-1.5 rounded-full bg-ring" />
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-52 p-0" align="end">
				{/* Sort section */}
				<div className="px-3 pt-3 pb-1.5">
					<span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
						Sort by
					</span>
				</div>
				<div className="px-1.5 pb-1.5">
					{sortOptions.map(({ value, label }) => (
						<button
							key={value}
							type="button"
							onClick={() => onSortChange(value)}
							className={cn(
								"flex items-center justify-between w-full px-2 py-1.5 rounded-md text-sm text-left transition-colors",
								sort === value
									? "bg-accent text-foreground"
									: "text-foreground/80 hover:bg-accent",
							)}
						>
							{label}
							{sort === value && (
								<Check className="size-3.5 text-primary shrink-0" />
							)}
						</button>
					))}
				</div>

				{/* Group section */}
				<div className="border-t border-border">
					<div className="px-3 pt-2.5 pb-1.5">
						<span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
							Group by
						</span>
					</div>
					<div className="px-1.5 pb-1.5">
						{groupOptions.map(({ value, label }) => (
							<button
								key={value}
								type="button"
								onClick={() => onGroupChange(value)}
								className={cn(
									"flex items-center justify-between w-full px-2 py-1.5 rounded-md text-sm text-left transition-colors",
									group === value
										? "bg-accent text-foreground"
										: "text-foreground/80 hover:bg-accent",
								)}
							>
								{label}
								{group === value && (
									<Check className="size-3.5 text-primary shrink-0" />
								)}
							</button>
						))}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}
